/* =====================================================
   MULTIVERSE BONDS
   Bondverse Product Builder + Result Page
   Step 5: Browser Persistence with IndexedDB
===================================================== */

(function () {

  'use strict';


  /* ===================================================
     BROWSER STORAGE
     IndexedDB is used instead of localStorage because
     generated PNGs and customer photos can be too large
     for localStorage's small quota.
  =================================================== */

  const BONDVERSE_DB_NAME =
    'multiverse-bonds-browser-storage';


  const BONDVERSE_DB_VERSION =
    1;


  const BONDVERSE_STORE_NAME =
    'bondverse-sessions';



  function openBondverseDatabase() {

    return new Promise(
      (resolve, reject) => {

        if (
          !('indexedDB' in window)
        ) {

          reject(
            new Error(
              'IndexedDB is not supported in this browser.'
            )
          );

          return;

        }


        const request =
          window.indexedDB.open(
            BONDVERSE_DB_NAME,
            BONDVERSE_DB_VERSION
          );


        request.onupgradeneeded =
          event => {

            const database =
              event.target.result;


            if (
              !database.objectStoreNames.contains(
                BONDVERSE_STORE_NAME
              )
            ) {

              database.createObjectStore(
                BONDVERSE_STORE_NAME,
                {
                  keyPath: 'key'
                }
              );

            }

          };


        request.onsuccess =
          () => {

            resolve(
              request.result
            );

          };


        request.onerror =
          () => {

            reject(
              request.error ||
              new Error(
                'Unable to open browser storage.'
              )
            );

          };

      }
    );

  }



  async function saveBondverseSession(
    session
  ) {

    const database =
      await openBondverseDatabase();


    return new Promise(
      (resolve, reject) => {

        const transaction =
          database.transaction(
            BONDVERSE_STORE_NAME,
            'readwrite'
          );


        const store =
          transaction.objectStore(
            BONDVERSE_STORE_NAME
          );


        store.put(
          session
        );


        transaction.oncomplete =
          () => {

            database.close();

            resolve();

          };


        transaction.onerror =
          () => {

            const error =
              transaction.error ||
              new Error(
                'Unable to save your Bondverse in browser storage.'
              );


            database.close();

            reject(
              error
            );

          };

      }
    );

  }



  async function getBondverseSession(
    key
  ) {

    const database =
      await openBondverseDatabase();


    return new Promise(
      (resolve, reject) => {

        const transaction =
          database.transaction(
            BONDVERSE_STORE_NAME,
            'readonly'
          );


        const store =
          transaction.objectStore(
            BONDVERSE_STORE_NAME
          );


        const request =
          store.get(
            key
          );


        request.onsuccess =
          () => {

            database.close();

            resolve(
              request.result ||
              null
            );

          };


        request.onerror =
          () => {

            const error =
              request.error ||
              new Error(
                'Unable to restore your saved Bondverse.'
              );


            database.close();

            reject(
              error
            );

          };

      }
    );

  }



  async function deleteBondverseSession(
    key
  ) {

    const database =
      await openBondverseDatabase();


    return new Promise(
      (resolve, reject) => {

        const transaction =
          database.transaction(
            BONDVERSE_STORE_NAME,
            'readwrite'
          );


        const store =
          transaction.objectStore(
            BONDVERSE_STORE_NAME
          );


        store.delete(
          key
        );


        transaction.oncomplete =
          () => {

            database.close();

            resolve();

          };


        transaction.onerror =
          () => {

            const error =
              transaction.error ||
              new Error(
                'Unable to clear your saved Bondverse.'
              );


            database.close();

            reject(
              error
            );

          };

      }
    );

  }



  /* ===================================================
     INITIALIZE
  =================================================== */

  function initAllBondverseSections(
    root = document
  ) {

    root
      .querySelectorAll(
        '[data-bondverse-section]'
      )
      .forEach(
        initBondverseSection
      );

  }


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      () => {

        initAllBondverseSections();

      }
    );

  } else {

    initAllBondverseSections();

  }


  /*
   * Shopify Theme Editor can reload a section
   * without reloading the entire page.
   */

  document.addEventListener(
    'shopify:section:load',
    event => {

      initAllBondverseSections(
        event.target
      );

    }
  );



  /* ===================================================
     SECTION
  =================================================== */

  function initBondverseSection(
    section
  ) {

    /*
     * Prevent duplicate listeners.
     */

    if (
      section.dataset.bondverseInitialized ===
      'true'
    ) {

      return;

    }


    section.dataset.bondverseInitialized =
      'true';



    /* -------------------------------------------------
       STORAGE KEY
       One saved generation per product URL.
    -------------------------------------------------- */

    const browserStorageKey =
      `bondverse-current:${window.location.pathname}`;



    /* -------------------------------------------------
       API CONFIG
    -------------------------------------------------- */

    const rawApiBaseUrl =
      (
        section.dataset.apiBaseUrl ||
        ''
      ).trim();


    const apiBaseUrl =
      rawApiBaseUrl.replace(
        /\/+$/,
        ''
      );


    const generatePath =
      section.dataset.generatePath ||
      '/api/bondverse/generate';


    const quotaPath =
      section.dataset.quotaPath ||
      '/api/bondverse/quota';


    const customerId =
      (
        section.dataset.customerId ||
        ''
      ).trim();


    const creditsUrl =
      (
        section.dataset.creditsUrl ||
        ''
      ).trim();


    const creditsVariantId =
      (
        section.dataset.creditsVariantId ||
        ''
      ).trim();


    const creditsPrice =
      (
        section.dataset.creditsPrice ||
        ''
      ).trim();


    const creditsTitle =
      (
        section.dataset.creditsTitle ||
        'Generation Credits'
      ).trim();


    const loginUrl =
      (
        section.dataset.loginUrl ||
        '/account/login'
      ).trim();


    const cartAddUrl =
      (
        section.dataset.cartAddUrl ||
        ''
      ).trim();


    const checkoutUrl =
      (
        section.dataset.checkoutUrl ||
        ''
      ).trim();



    /* -------------------------------------------------
       CREATE PAGE ELEMENTS
    -------------------------------------------------- */

    const createStep =
      section.querySelector(
        '[data-bondverse-step="create"]'
      );


    const resultStep =
      section.querySelector(
        '[data-bondverse-step="result"]'
      );


    const scrollButton =
      section.querySelector(
        '[data-scroll-to-builder]'
      );


    const builder =
      section.querySelector(
        '#bondverse-builder'
      );


    const photoInput =
      section.querySelector(
        '[data-photo-input]'
      );


    const uploadTitle =
      section.querySelector(
        '[data-upload-title]'
      );


    const uploadedPreview =
      section.querySelector(
        '[data-upload-preview]'
      );


    const examplePhoto =
      section.querySelector(
        '.bondverse__example-photo'
      );


    const generatedPreview =
      section.querySelector(
        '[data-generated-preview]'
      );


    const exampleArtwork =
      section.querySelector(
        '.bondverse__example-artwork'
      );


    const generationLoading =
      section.querySelector(
        '[data-generation-loading]'
      );


    const generationIdInput =
      section.querySelector(
        '[data-generation-id]'
      );


    const form =
      section.querySelector(
        '[data-bondverse-form]'
      );


    const formMessage =
      section.querySelector(
        '[data-form-message]'
      );


    const generateButton =
      section.querySelector(
        '[data-generate-button]'
      );


    const generateButtonText =
      section.querySelector(
        '[data-generate-button-text]'
      );


    const quotaBox =
      section.querySelector(
        '[data-generation-quota]'
      );


    const quotaTitle =
      section.querySelector(
        '[data-quota-title]'
      );


    const quotaSubtitle =
      section.querySelector(
        '[data-quota-subtitle]'
      );


    const quotaProgress =
      section.querySelector(
        '[data-quota-progress]'
      );


    const buyCreditsLink =
      section.querySelector(
        '[data-buy-generation-credits]'
      );


    const quotaActionText =
      section.querySelector(
        '[data-quota-action-text]'
      );


    const savedReturnBar =
      section.querySelector(
        '[data-saved-bondverse-return]'
      );


    const viewSavedButton =
      section.querySelector(
        '[data-view-saved-bondverse]'
      );



    /* -------------------------------------------------
       RESULT PAGE ELEMENTS
    -------------------------------------------------- */

    const backButton =
      section.querySelector(
        '[data-bondverse-back]'
      );


    const generateAnotherButton =
      section.querySelector(
        '[data-bondverse-generate-another]'
      );


    const resultImage =
      section.querySelector(
        '[data-result-image]'
      );


    const resultThumb =
      section.querySelector(
        '[data-result-thumb]'
      );


    const resultOriginal =
      section.querySelector(
        '[data-result-original]'
      );


    const resultVibe =
      section.querySelector(
        '[data-result-vibe]'
      );


    const resultStory =
      section.querySelector(
        '[data-result-story]'
      );


    const resultStoryRow =
      section.querySelector(
        '[data-result-story-row]'
      );


    const resultUniverse =
      section.querySelector(
        '[data-result-universe]'
      );


    const resultUniverseRow =
      section.querySelector(
        '[data-result-universe-row]'
      );


    const resultGenerationId =
      section.querySelector(
        '[data-result-generation-id]'
      );


    const packageInputs =
      section.querySelectorAll(
        '[data-package-option]'
      );


    const resultViewButtons =
      section.querySelectorAll(
        '[data-result-view]'
      );


    const addCartButton =
      section.querySelector(
        '[data-bondverse-add-cart]'
      );


    const cartMessage =
      section.querySelector(
        '[data-result-cart-message]'
      );


    const variantIdInput =
      section.querySelector(
        '[data-bondverse-variant-id]'
      );





    /* -------------------------------------------------
       LOCAL STATE
    -------------------------------------------------- */

    let currentPhotoObjectUrl =
      null;


    /*
     * Browser security does not allow JavaScript
     * to repopulate a file input after refresh.
     *
     * We therefore keep the restored photo Blob here
     * and use it as a generation fallback if the
     * customer goes back and generates again.
     */

    let savedOriginalPhotoBlob =
      null;


    let savedOriginalPhotoName =
      '';


    let generatedState = {

      image:
        '',

      artworkUrl:
        '',

      originalPhoto:
        '',

      generationId:
        '',

      vibe:
        '',

      bondSpecial:
        '',

      universeDetails:
        ''

    };


    let quotaState =
      null;


    let quotaRequestInFlight =
      false;



    /* =================================================
       HERO CTA → BUILDER
    ================================================= */

    if (
      scrollButton &&
      builder
    ) {

      scrollButton.addEventListener(
        'click',
        () => {

          builder.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });

        }
      );

    }



    /* =================================================
       QUOTA ACTION
       Guest  -> Shopify sign in -> return to this page
       Member -> add credits product -> direct checkout
    ================================================= */

    if (
      buyCreditsLink
    ) {

      buyCreditsLink.addEventListener(
        'click',
        async event => {

          const actionType =
            buyCreditsLink.dataset.quotaActionType ||
            '';


          if (
            actionType ===
            'login'
          ) {

            /*
             * The href is already populated with Shopify's
             * login URL + return_url, so normal navigation
             * preserves native Shopify account behavior.
             */

            return;

          }


          if (
            actionType !==
            'checkout'
          ) {

            event.preventDefault();

            return;

          }


          event.preventDefault();


          await startCreditsCheckout();

        }
      );

    }



    /* =================================================
       CHARACTER COUNTERS
    ================================================= */

    section
      .querySelectorAll(
        '[data-character-field]'
      )
      .forEach(
        field => {

          const wrap =
            field.closest(
              '.bondverse__textarea-wrap'
            );


          const count =
            wrap?.querySelector(
              '[data-character-count]'
            );


          if (
            !count
          ) {

            return;

          }


          updateCount();


          field.addEventListener(
            'input',
            updateCount
          );


          function updateCount() {

            count.textContent =
              String(
                field.value.length
              );

          }

        }
      );



    /* =================================================
       CUSTOMER PHOTO PREVIEW
    ================================================= */

    if (
      photoInput &&
      uploadedPreview
    ) {

      photoInput.addEventListener(
        'change',
        () => {

          const file =
            photoInput.files?.[0];


          if (
            !file
          ) {

            return;

          }


          if (
            !isSupportedImage(
              file
            )
          ) {

            showMessage(
              'Please upload a JPG, PNG, WEBP, HEIC or HEIF image.',
              'error'
            );


            photoInput.value =
              '';


            return;

          }


          const maxSize =
            12 *
            1024 *
            1024;


          if (
            file.size >
            maxSize
          ) {

            showMessage(
              'Please choose an image smaller than 12MB.',
              'error'
            );


            photoInput.value =
              '';


            return;

          }


          setOriginalPhotoPreview(
            file,
            file.name
          );


          /*
           * This photo can now be used whether
           * it came from the file input or is
           * later restored from IndexedDB.
           */

          savedOriginalPhotoBlob =
            file;


          savedOriginalPhotoName =
            file.name;


          /*
           * The customer has edited the input.
           * Hide the previous generated preview,
           * but do not delete their saved completed
           * generation until they explicitly choose
           * Generate Another or successfully replace it
           * with a new generation.
           */

          hideCurrentGeneratedPreview();


          showMessage(
            'Photo added ✓',
            'success'
          );

        }
      );

    }



    /* =================================================
       GENERATE BONDVERSE
    ================================================= */

    if (
      form
    ) {

      form.addEventListener(
        'submit',
        async event => {

          event.preventDefault();



          /* -----------------------------
             CHECK BACKEND URL
          ------------------------------ */

          if (
            !apiBaseUrl
          ) {

            showMessage(
              'Backend URL is not configured yet. Add your Cloudflare URL in the Shopify Theme Editor.',
              'error'
            );

            return;

          }



          /* -----------------------------
             GET PHOTO
          ------------------------------ */

          const uploadedFile =
            photoInput?.files?.[0] ||
            null;


          /*
           * After a refresh the file input must be
           * empty, but the Blob restored from
           * IndexedDB can still be sent to FastAPI.
           */

          const photo =
            uploadedFile ||
            savedOriginalPhotoBlob;


          if (
            !photo
          ) {

            showMessage(
              'Please upload your photo first.',
              'error'
            );


            photoInput?.focus();

            return;

          }


          if (
            !isSupportedImage(
              photo
            )
          ) {

            showMessage(
              'Please upload a supported image file.',
              'error'
            );

            return;

          }



          /* -----------------------------
             CUSTOMER ANSWERS
          ------------------------------ */

          const vibe =
            form.querySelector(
              'input[name="relationship_vibe"]:checked'
            )?.value ||
            'Romantic';


          const bondSpecial =
            form.querySelector(
              '[name="bond_special"]'
            )?.value.trim() ||
            '';


          const universeDetails =
            form.querySelector(
              '[name="universe_details"]'
            )?.value.trim() ||
            '';



          /* -----------------------------
             MULTIPART REQUEST
          ------------------------------ */

          const apiData =
            new FormData();


          /*
           * Supplying an explicit filename also works
           * when "photo" is a restored Blob.
           */

          apiData.append(
            'photo',
            photo,
            uploadedFile?.name ||
            savedOriginalPhotoName ||
            'bondverse-photo.png'
          );


          apiData.append(
            'vibe',
            vibe
          );


          apiData.append(
            'bond_special',
            bondSpecial
          );


          apiData.append(
            'universe_details',
            universeDetails
          );


          if (
            customerId
          ) {

            apiData.append(
              'customer_id',
              customerId
            );

          }



          /* -----------------------------
             LOADING
          ------------------------------ */

          setGenerating(
            true
          );


          showMessage(
            'Creating your Bondverse. This can take a little while ✨',
            'info'
          );


          const controller =
            new AbortController();


          const timeoutId =
            window.setTimeout(
              () => {

                controller.abort();

              },
              360000
            );



          try {

            /* -----------------------------
               FASTAPI REQUEST
            ------------------------------ */

            const response =
              await fetch(
                `${apiBaseUrl}${generatePath}`,
                {
                  method: 'POST',
                  body: apiData,
                  signal: controller.signal
                }
              );


            const result =
              await parseApiResponse(
                response
              );


            if (
              !response.ok
            ) {

              if (
                response.status === 429 &&
                result?.code ===
                  'generation_limit_reached'
              ) {

                if (
                  result?.quota
                ) {

                  updateQuotaUI(
                    result.quota
                  );

                }


                const limitError =
                  new Error(
                    result?.message ||
                    'You have reached today\'s free generation limit.'
                  );


                limitError.code =
                  'generation_limit_reached';


                throw limitError;

              }


              const apiDetail =
                typeof result?.detail ===
                'string'
                  ? result.detail
                  : result?.detail?.message;


              throw new Error(
                apiDetail ||
                result?.message ||
                `Generation failed (${response.status}).`
              );

            }


            if (
              !result?.image
            ) {

              throw new Error(
                'The backend responded successfully but did not return an image.'
              );

            }



            /* -----------------------------
               DAILY QUOTA
            ------------------------------ */

            if (
              result?.quota
            ) {

              updateQuotaUI(
                result.quota
              );

            }


            /* -----------------------------
               SHOW CREATE-PAGE PREVIEW
            ------------------------------ */

            if (
              generatedPreview
            ) {

              generatedPreview.src =
                result.image;


              generatedPreview.hidden =
                false;

            }


            if (
              generationLoading
            ) {

              generationLoading.hidden =
                true;

            }


            if (
              exampleArtwork
            ) {

              exampleArtwork.hidden =
                true;

            }



            /* -----------------------------
               GENERATION ID
            ------------------------------ */

            const generationId =
              result.generation_id ||
              '';


            form.dataset.generationId =
              generationId;


            if (
              generationIdInput
            ) {

              generationIdInput.value =
                generationId;

            }



            /* -----------------------------
               RESULT STATE
            ------------------------------ */

            const artworkUrl =
              result.artwork_path
                ? `${apiBaseUrl}${result.artwork_path}`
                : '';


            generatedState = {

              image:
                result.image,

              artworkUrl:
                artworkUrl,

              originalPhoto:
                currentPhotoObjectUrl,

              generationId:
                generationId,

              vibe:
                vibe,

              bondSpecial:
                bondSpecial,

              universeDetails:
                universeDetails

            };


            savedOriginalPhotoBlob =
              photo;


            savedOriginalPhotoName =
              uploadedFile?.name ||
              savedOriginalPhotoName ||
              'bondverse-photo.png';



            /* -----------------------------
               BUILD RESULT PAGE
            ------------------------------ */

            populateResultPage(
              generatedState
            );



            /* -----------------------------
               SAVE TO INDEXEDDB
            ------------------------------ */

            try {

              await saveCurrentGenerationToBrowser();

              console.log(
                '[Multiverse Bonds] Generation saved in browser storage.'
              );

            } catch (
              storageError
            ) {

              /*
               * Storage failure must never destroy
               * a successful paid-generation experience.
               */

              console.warn(
                '[Multiverse Bonds] Could not save generation in browser storage:',
                storageError
              );

            }



            /* -----------------------------
               CUSTOM EVENT
            ------------------------------ */

            form.dispatchEvent(
              new CustomEvent(
                'bondverse:generated',
                {
                  bubbles: true,

                  detail: {

                    generationId:
                      generationId,

                    image:
                      result.image,

                    artworkUrl:
                      artworkUrl,

                    vibe:
                      vibe,

                    bondSpecial:
                      bondSpecial,

                    universeDetails:
                      universeDetails

                  }

                }
              )
            );


            showMessage(
              'Your Bondverse is ready ✨',
              'success'
            );


            console.log(
              '[Multiverse Bonds] Generation complete:',
              generationId
            );



            /* -----------------------------
               RESULT PAGE
            ------------------------------ */

            showResultPage({
              pushHistory: true
            });


          } catch (
            error
          ) {

            console.error(
              '[Multiverse Bonds] Generation error:',
              error
            );


            if (
              generationLoading
            ) {

              generationLoading.hidden =
                true;

            }


            /*
             * If a previous successful generation
             * exists, leave it safely stored.
             */

            if (
              !generatedState.image
            ) {

              if (
                generatedPreview
              ) {

                generatedPreview.hidden =
                  true;

                generatedPreview.removeAttribute(
                  'src'
                );

              }


              if (
                exampleArtwork
              ) {

                exampleArtwork.hidden =
                  false;

              }

            }


            if (
              error.name ===
              'AbortError'
            ) {

              showMessage(
                'Your generation took too long. Please try again.',
                'error'
              );

            } else if (
              error.code ===
              'generation_limit_reached'
            ) {

              showMessage(
                error.message ||
                'You have reached today\'s free generation limit.',
                'error'
              );

            } else {

              showMessage(
                error.message ||
                'Something went wrong. Please try again.',
                'error'
              );

            }


          } finally {

            window.clearTimeout(
              timeoutId
            );


            setGenerating(
              false
            );

          }

        }
      );

    }



    /* =================================================
       RESULT PAGE BACK BUTTON
       Back preserves the finished generation.
    ================================================= */

    if (
      backButton
    ) {

      backButton.addEventListener(
        'click',
        () => {

          if (
            window.location.hash ===
            '#your-bondverse'
          ) {

            window.history.back();

          } else {

            showCreatePage();

          }

        }
      );

    }



    /* =================================================
       VIEW SAVED BONDVERSE
       Lets the customer return from editing to the
       already-generated product/result page.
    ================================================= */

    if (
      viewSavedButton
    ) {

      viewSavedButton.addEventListener(
        'click',
        () => {

          if (
            !generatedState.image
          ) {

            showMessage(
              'Your saved Bondverse is no longer available in this browser.',
              'error'
            );

            return;

          }


          showResultPage({
            pushHistory:
              true
          });

        }
      );

    }



    /* =================================================
       GENERATE ANOTHER
       This is the explicit reset requested by the user.
    ================================================= */

    if (
      generateAnotherButton
    ) {

      generateAnotherButton.addEventListener(
        'click',
        async () => {

          const confirmed =
            window.confirm(
              'Start a new Bondverse? Your currently saved browser preview will be cleared.'
            );


          if (
            !confirmed
          ) {

            return;

          }


          try {

            await deleteBondverseSession(
              browserStorageKey
            );

          } catch (
            error
          ) {

            console.warn(
              '[Multiverse Bonds] Browser session could not be deleted:',
              error
            );

          }


          resetEverythingForNewGeneration();


          const cleanUrl =
            window.location.pathname +
            window.location.search;


          window.history.replaceState(
            {
              bondversePage:
                'create'
            },
            '',
            cleanUrl
          );


          showCreatePage({
            smooth: false
          });


          window.setTimeout(
            () => {

              builder?.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });

            },
            40
          );

        }
      );

    }



    /* =================================================
       BROWSER BACK / FORWARD
    ================================================= */

    window.addEventListener(
      'popstate',
      () => {

        const wantsResult =
          window.location.hash ===
          '#your-bondverse';


        if (
          wantsResult &&
          generatedState.image
        ) {

          showResultPage({
            pushHistory: false,
            smooth: false
          });

        } else {

          showCreatePage({
            smooth: false
          });

        }

      }
    );



    /* =================================================
       RESULT THUMBNAILS
    ================================================= */

    resultViewButtons.forEach(
      button => {

        button.addEventListener(
          'click',
          () => {

            const view =
              button.dataset.resultView;


            resultViewButtons.forEach(
              item => {

                item.classList.toggle(
                  'is-active',
                  item === button
                );

              }
            );


            if (
              !resultImage
            ) {

              return;

            }


            if (
              view ===
                'original' &&
              generatedState.originalPhoto
            ) {

              resultImage.src =
                generatedState.originalPhoto;


              resultImage.alt =
                'Your original uploaded couple photo';

            } else {

              resultImage.src =
                generatedState.image;


              resultImage.alt =
                'Your personalized Bondverse Couple Keychain';

            }

          }
        );

      }
    );



    /* =================================================
       PACKAGE SELECTION
       1 = single keychain
       2 = couple / matching set
       4 = set of four
    ================================================= */

    packageInputs.forEach(
      input => {

        input.addEventListener(
          'change',
          () => {

            if (
              input.checked
            ) {

              saveCurrentGenerationToBrowserQuietly();

            }

          }
        );

      }
    );



    /* =================================================
       SHOPIFY ADD TO CART
    ================================================= */

    if (
      addCartButton
    ) {

      addCartButton.addEventListener(
        'click',
        async () => {


          /* -----------------------------
             REQUIRE GENERATED DESIGN
          ------------------------------ */

          if (
            !generatedState.generationId ||
            !generatedState.image
          ) {

            showCartMessage(
              'Please create your Bondverse first.',
              'error'
            );

            return;

          }


          /*
           * During development the backend
           * must keep generated PNG files locally.
           */

          if (
            !generatedState.artworkUrl
          ) {

            showCartMessage(
              'Your artwork file URL is missing. Make sure KEEP_GENERATED_FILES=true in your backend .env, then generate the design again.',
              'error'
            );

            return;

          }


          /* -----------------------------
             SHOPIFY VARIANT
          ------------------------------ */

          const variantId =
            Number(
              variantIdInput?.value
            );


          if (
            !variantId
          ) {

            console.error(
              '[Multiverse Bonds] Variant ID missing.'
            );


            showCartMessage(
              'This Shopify product is not configured correctly yet. Make sure this section is used on the Bondverse product page.',
              'error'
            );

            return;

          }


          /* -----------------------------
             QUANTITY
          ------------------------------ */

          const finalQuantity =
            getQuantity();


          const matchingCopy =
            finalQuantity > 1;



          /* -----------------------------
             LINE ITEM PROPERTIES
          ------------------------------ */

          const properties = {

            'Personalization':
              'Custom Bondverse Artwork',

            '_generation_id':
              generatedState.generationId,

            '_artwork_url':
              generatedState.artworkUrl,

            '_relationship_vibe':
              generatedState.vibe,

            '_matching_set':
              matchingCopy
                ? 'Yes'
                : 'No'

          };


          if (
            generatedState.bondSpecial
          ) {

            properties[
              '_bond_story'
            ] =
              generatedState.bondSpecial;

          }


          if (
            generatedState.universeDetails
          ) {

            properties[
              '_universe_details'
            ] =
              generatedState.universeDetails;

          }



          /* -----------------------------
             BUTTON STATE
          ------------------------------ */

          const cartButtonText =
            addCartButton.querySelector(
              'span'
            );


          const oldButtonText =
            cartButtonText?.textContent ||
            'Add Your Bondverse to Cart';


          addCartButton.disabled =
            true;


          if (
            cartButtonText
          ) {

            cartButtonText.textContent =
              'Adding Your Bondverse...';

          }


          showCartMessage(
            'Saving your personalized design...',
            'info'
          );



          try {

            /* -----------------------------
               SHOPIFY AJAX CART API
            ------------------------------ */

            const shopRoot =
              window.Shopify?.routes?.root ||
              '/';


            const cartUrl =
              `${shopRoot}cart/add.js`;


            /*
             * SHIPPING PROTECTION
             *
             * Resolve the real Shopify Shipping Protection variant
             * before dispatching Horizon's cart event. If protection
             * is not already in the cart, it is included in the same
             * /cart/add.js request as the Bondverse item.
             */

            let shippingProtectionVariantId =
              null;


            let shippingProtectionAlreadyInCart =
              false;


            try {

              const [
                protectionProductResponse,
                currentCartResponse
              ] =
                await Promise.all([

                  fetch(
                    `${shopRoot}products/shipping-protection.js`,
                    {
                      headers: {
                        'Accept':
                          'application/json'
                      },
                      credentials:
                        'same-origin',
                      cache:
                        'no-store'
                    }
                  ),

                  fetch(
                    `${shopRoot}cart.js`,
                    {
                      headers: {
                        'Accept':
                          'application/json'
                      },
                      credentials:
                        'same-origin',
                      cache:
                        'no-store'
                    }
                  )

                ]);


              if (
                protectionProductResponse.ok
              ) {

                const protectionProduct =
                  await protectionProductResponse.json();


                const protectionVariant =
                  protectionProduct?.variants?.find(
                    variant =>
                      variant.available
                  ) ||
                  protectionProduct?.variants?.[0] ||
                  null;


                if (
                  protectionVariant?.id
                ) {

                  shippingProtectionVariantId =
                    Number(
                      protectionVariant.id
                    );

                }

              }


              if (
                currentCartResponse.ok &&
                shippingProtectionVariantId
              ) {

                const currentCart =
                  await currentCartResponse.json();


                shippingProtectionAlreadyInCart =
                  Boolean(
                    currentCart?.items?.some(
                      item =>
                        Number(
                          item.variant_id
                        ) ===
                        shippingProtectionVariantId
                    )
                  );

              }

            } catch (
              protectionLookupError
            ) {

              console.warn(
                '[Multiverse Bonds] Shipping Protection lookup failed. The Bondverse item will still be added:',
                protectionLookupError
              );

            }


            const cartItems = [

              {

                id:
                  variantId,

                quantity:
                  finalQuantity,

                properties:
                  properties

              }

            ];


            if (
              shippingProtectionVariantId &&
              !shippingProtectionAlreadyInCart
            ) {

              cartItems.push({

                id:
                  shippingProtectionVariantId,

                quantity:
                  1,

                properties: {
                  '_shipping_protection':
                    'Yes'
                }

              });

            }


            const cartEventLines =
              cartItems.map(
                item => ({

                  merchandiseId:
                    String(
                      item.id
                    ),

                  quantity:
                    Number(
                      item.quantity
                    )

                })
              );


            /*
             * Horizon's cart UI listens for Shopify's standard
             * CartLinesUpdateEvent. We create that event before
             * the AJAX request, then resolve it with the fresh
             * section HTML after Shopify confirms the add.
             */

            let CartLinesUpdateEvent =
              null;


            let deferredCartUpdate =
              null;


            try {

              const shopifyEvents =
                await import(
                  '@shopify/events'
                );


              CartLinesUpdateEvent =
                shopifyEvents.CartLinesUpdateEvent;


              deferredCartUpdate =
                CartLinesUpdateEvent.createPromise();


              document.dispatchEvent(
                new CartLinesUpdateEvent({

                  action:
                    'add',

                  context:
                    'product',

                  lines:
                    cartEventLines,

                  promise:
                    deferredCartUpdate.promise

                })
              );

            } catch (eventImportError) {

              console.warn(
                '[Multiverse Bonds] Shopify cart event module was unavailable:',
                eventImportError
              );

            }


            /*
             * Ask Shopify for the exact cart sections that the
             * current Horizon theme has mounted. The cart drawer
             * uses data-section-id="cart-drawer-section".
             */

            const cartSectionIds =
              Array.from(
                document.querySelectorAll(
                  'cart-items-component[data-section-id]'
                )
              )
                .map(
                  component =>
                    component.dataset.sectionId
                )
                .filter(Boolean);


            if (
              !cartSectionIds.includes(
                'cart-drawer-section'
              )
            ) {

              cartSectionIds.push(
                'cart-drawer-section'
              );

            }


            const response =
              await fetch(
                cartUrl,
                {

                  method:
                    'POST',

                  headers: {

                    'Content-Type':
                      'application/json',

                    'Accept':
                      'application/json'

                  },

                  credentials:
                    'same-origin',

                  body:
                    JSON.stringify({

                      items:
                        cartItems,

                      sections:
                        cartSectionIds.join(','),

                      sections_url:
                        window.location.pathname


                    })

                }
              );


            const result =
              await parseShopifyResponse(
                response
              );


            if (
              !response.ok
            ) {

              throw new Error(
                result?.description ||
                result?.message ||
                `Unable to add this item to the cart (${response.status}).`
              );

            }



            /* -----------------------------
               SUCCESS
            ------------------------------ */

            console.log(
              '[Multiverse Bonds] Added to cart:',
              result
            );


            showCartMessage(
              'Your Bondverse was added to your cart ✓',
              'success'
            );


            /*
             * The browser-saved generation is NOT
             * cleared here. It remains available
             * until Generate Another is selected.
             */

            /* -----------------------------
               REFRESH + OPEN HORIZON CART DRAWER
            ------------------------------ */

            let ajaxCart =
              null;


            try {

              const cartResponse =
                await fetch(
                  `${shopRoot}cart.js`,
                  {
                    headers: {
                      'Accept':
                        'application/json'
                    },
                    credentials:
                      'same-origin',
                    cache:
                      'no-store'
                  }
                );


              if (
                cartResponse.ok
              ) {

                ajaxCart =
                  await cartResponse.json();

              }

            } catch (cartRefreshError) {

              console.warn(
                '[Multiverse Bonds] Could not fetch refreshed cart JSON:',
                cartRefreshError
              );

            }


            if (
              deferredCartUpdate &&
              CartLinesUpdateEvent
            ) {

              deferredCartUpdate.resolve({

                cart:
                  ajaxCart
                    ? CartLinesUpdateEvent.createCartFromAjaxResponse(
                        ajaxCart
                      )
                    : undefined,

                detail: {

                  items:
                    ajaxCart?.items ||
                    result?.items ||
                    [],

                  source:
                    'bondverse-product-builder',

                  sourceId:
                    section.id ||
                    'bondverse-product-builder',

                  itemCount:
                    cartEventLines.reduce(
                      (
                        total,
                        line
                      ) =>
                        total +
                        Number(
                          line.quantity ||
                          0
                        ),
                      0
                    ),

                  sections:
                    result?.sections ||
                    {},

                  didError:
                    false

                }

              });


              /*
               * Give Horizon one paint cycle to morph the
               * returned cart-drawer-section before opening it.
               */

              await new Promise(
                resolve =>
                  requestAnimationFrame(
                    () =>
                      requestAnimationFrame(
                        resolve
                      )
                  )
              );

            }


            if (
              window.Shopify?.actions?.openCart
            ) {

              window.Shopify.actions.openCart();

            } else {

              const horizonDrawer =
                document.querySelector(
                  'theme-drawer#cart-drawer'
                );


              if (
                horizonDrawer &&
                typeof horizonDrawer.open ===
                  'function'
              ) {

                horizonDrawer.open();

              } else {

                const cartTrigger =
                  document.querySelector(
                    '.header-actions__cart-icon, [href$="/cart"]'
                  );


                if (
                  cartTrigger
                ) {

                  cartTrigger.click();

                } else {

                  console.warn(
                    '[Multiverse Bonds] Cart updated, but the Horizon cart drawer could not be opened.'
                  );

                }

              }

            }


          } catch (
            error
          ) {

            try {

              deferredCartUpdate?.reject?.(
                error
              );

            } catch (eventRejectError) {

              console.warn(
                '[Multiverse Bonds] Cart event rejection warning:',
                eventRejectError
              );

            }

            console.error(
              '[Multiverse Bonds] Cart error:',
              error
            );


            showCartMessage(
              error.message ||
              'We could not add your Bondverse to the cart. Please try again.',
              'error'
            );


          } finally {

            addCartButton.disabled =
              false;


            if (
              cartButtonText
            ) {

              cartButtonText.textContent =
                oldButtonText;

            }

          }

        }
      );

    }



    /* =================================================
       DAILY GENERATION QUOTA
    ================================================= */

    refreshQuota();


    /* =================================================
       RESTORE SAVED GENERATION
    ================================================= */

    restoreSavedGeneration();



    /* =================================================
       HELPERS
    ================================================= */


    function buildLoginReturnUrl() {

      const currentPage =
        window.location.pathname +
        window.location.search;


      try {

        const target =
          new URL(
            loginUrl,
            window.location.origin
          );


        target.searchParams.set(
          'return_url',
          currentPage
        );


        return target.toString();


      } catch (
        error
      ) {

        const separator =
          loginUrl.includes(
            '?'
          )
            ? '&'
            : '?';


        return (
          loginUrl +
          separator +
          'return_url=' +
          encodeURIComponent(
            currentPage
          )
        );

      }

    }



    async function startCreditsCheckout() {

      /*
       * Purchased credits are tied to a Shopify customer,
       * so guests must authenticate first.
       */

      if (
        !customerId
      ) {

        window.location.assign(
          buildLoginReturnUrl()
        );

        return;

      }


      /*
       * If only a fallback product URL is configured,
       * send the customer there instead of failing.
       */

      if (
        !creditsVariantId
      ) {

        if (
          creditsUrl
        ) {

          window.location.assign(
            creditsUrl
          );

          return;

        }


        showMessage(
          'Generation credits are not configured yet.',
          'error'
        );

        return;

      }


      const parsedVariantId =
        Number(
          creditsVariantId
        );


      if (
        !Number.isFinite(
          parsedVariantId
        ) ||
        parsedVariantId <= 0
      ) {

        showMessage(
          'The generation credits product is not configured correctly.',
          'error'
        );

        return;

      }


      const oldActionText =
        quotaActionText?.textContent ||
        'Get More Generations';


      try {

        if (
          buyCreditsLink
        ) {

          buyCreditsLink.classList.add(
            'is-loading'
          );


          buyCreditsLink.setAttribute(
            'aria-disabled',
            'true'
          );

        }


        if (
          quotaActionText
        ) {

          quotaActionText.textContent =
            'Preparing checkout...';

        }


        const shopifyRoot =
          window.Shopify?.routes?.root ||
          '/';


        const endpoint =
          cartAddUrl ||
          `${shopifyRoot}cart/add.js`;


        const response =
          await fetch(
            endpoint,
            {
              method:
                'POST',

              headers: {
                'Content-Type':
                  'application/json',

                'Accept':
                  'application/json'
              },

              body:
                JSON.stringify({
                  items: [
                    {
                      id:
                        parsedVariantId,

                      quantity:
                        1,

                      properties: {
                        '_bondverse_credit_pack':
                          'Generation Credits',

                        '_bondverse_credit_product':
                          creditsTitle
                      }
                    }
                  ]
                })
            }
          );


        const result =
          await parseShopifyResponse(
            response
          );


        if (
          !response.ok
        ) {

          throw new Error(
            result?.description ||
            result?.message ||
            'We could not add generation credits to checkout.'
          );

        }


        /*
         * Shopify checkout reads the cart, so the credits
         * product appears there with its live Shopify price.
         */

        window.location.assign(
          checkoutUrl ||
          `${shopifyRoot}checkout`
        );


      } catch (
        error
      ) {

        console.error(
          '[Multiverse Bonds] Credits checkout error:',
          error
        );


        showMessage(
          error.message ||
          'We could not open the credits checkout. Please try again.',
          'error'
        );


        if (
          buyCreditsLink
        ) {

          buyCreditsLink.classList.remove(
            'is-loading'
          );


          buyCreditsLink.removeAttribute(
            'aria-disabled'
          );

        }


        if (
          quotaActionText
        ) {

          quotaActionText.textContent =
            oldActionText;

        }

      }

    }



    async function refreshQuota() {

      if (
        !apiBaseUrl ||
        quotaRequestInFlight
      ) {

        return;

      }


      quotaRequestInFlight =
        true;


      try {

        const quotaUrl =
          new URL(
            `${apiBaseUrl}${quotaPath}`
          );


        if (
          customerId
        ) {

          quotaUrl.searchParams.set(
            'customer_id',
            customerId
          );

        }


        const response =
          await fetch(
            quotaUrl.toString(),
            {
              method:
                'GET',

              headers: {
                'Accept':
                  'application/json'
              }
            }
          );


        const result =
          await parseApiResponse(
            response
          );


        if (
          !response.ok
        ) {

          throw new Error(
            result?.detail ||
            result?.message ||
            'Unable to check generation allowance.'
          );

        }


        if (
          result?.quota
        ) {

          updateQuotaUI(
            result.quota
          );

        }


      } catch (
        error
      ) {

        /*
         * A quota-status display failure should not
         * break the product builder. The generation
         * endpoint still enforces the limit server-side.
         */

        console.warn(
          '[Multiverse Bonds] Could not load quota status:',
          error
        );


        if (
          quotaBox
        ) {

          quotaBox.hidden =
            true;

        }


      } finally {

        quotaRequestInFlight =
          false;

      }

    }



    function updateQuotaUI(
      quota
    ) {

      if (
        !quota
      ) {

        return;

      }


      quotaState =
        quota;


      if (
        !quotaBox
      ) {

        return;

      }


      const dailyLimit =
        Number(
          quota.daily_limit
        ) ||
        5;


      const freeRemaining =
        Math.max(
          0,
          Number(
            quota.free_remaining
          ) ||
          0
        );


      const paidCredits =
        Math.max(
          0,
          Number(
            quota.paid_credits
          ) ||
          0
        );


      const usablePaidCredits =
        Math.max(
          0,
          Number(
            quota.usable_paid_credits
          ) ||
          0
        );


      const canGenerate =
        Boolean(
          quota.can_generate
        );


      if (
        quotaProgress
      ) {

        const progressPercent =
          Math.max(
            0,
            Math.min(
              100,
              (
                freeRemaining /
                Math.max(
                  1,
                  dailyLimit
                )
              ) *
              100
            )
          );


        quotaProgress.style.width =
          `${progressPercent}%`;

      }


      const isLoggedIn =
        Boolean(
          customerId
        ) ||
        Boolean(
          quota.logged_in
        );


      quotaBox.hidden =
        false;


      quotaBox.classList.toggle(
        'is-low',
        freeRemaining > 0 &&
        freeRemaining <= 2
      );


      quotaBox.classList.toggle(
        'is-empty',
        freeRemaining === 0
      );


      if (
        quotaTitle
      ) {

        if (
          freeRemaining > 0
        ) {

          quotaTitle.textContent =
            `${freeRemaining} of ${dailyLimit} free generation${freeRemaining === 1 ? '' : 's'} left today`;

        } else if (
          usablePaidCredits > 0
        ) {

          quotaTitle.textContent =
            `${usablePaidCredits} purchased generation credit${usablePaidCredits === 1 ? '' : 's'} available`;

        } else {

          quotaTitle.textContent =
            'Daily free generation limit reached';

        }

      }


      if (
        quotaSubtitle
      ) {

        if (
          freeRemaining > 0
        ) {

          quotaSubtitle.textContent =
            'Your allowance resets automatically each day.';

        } else if (
          usablePaidCredits > 0
        ) {

          quotaSubtitle.textContent =
            'Your next generation will use one purchased credit.';

        } else if (
          paidCredits > 0 &&
          !quota.paid_credits_enabled
        ) {

          quotaSubtitle.textContent =
            `${paidCredits} purchased generation credit${paidCredits === 1 ? ' is' : 's are'} on your account. Secure credit verification still needs to be enabled before ${paidCredits === 1 ? 'it can' : 'they can'} be used.`;

        } else if (
          isLoggedIn
        ) {

          quotaSubtitle.textContent =
            `You've used today's ${dailyLimit} free generations. Add a generation pack to keep creating today.`;

        } else {

          quotaSubtitle.textContent =
            `You've used today's ${dailyLimit} free generations. Sign in to access purchased generations or get more.`;

        }

      }


      if (
        buyCreditsLink
      ) {

        buyCreditsLink.classList.remove(
          'is-login',
          'is-checkout',
          'is-loading'
        );


        buyCreditsLink.removeAttribute(
          'aria-disabled'
        );


        buyCreditsLink.dataset.quotaActionType =
          '';


        /*
         * If free allowance or usable paid credits remain,
         * there is no need to show a quota action.
         */

        if (
          freeRemaining > 0 ||
          usablePaidCredits > 0
        ) {

          buyCreditsLink.hidden =
            true;

        } else if (
          paidCredits > 0 &&
          !quota.paid_credits_enabled
        ) {

          /*
           * Do not encourage someone to buy the same credits
           * again while their existing balance is waiting on
           * secure account-credit verification.
           */

          buyCreditsLink.hidden =
            true;

        } else if (
          !isLoggedIn
        ) {

          buyCreditsLink.hidden =
            false;


          buyCreditsLink.classList.add(
            'is-login'
          );


          buyCreditsLink.dataset.quotaActionType =
            'login';


          buyCreditsLink.href =
            buildLoginReturnUrl();


          if (
            quotaActionText
          ) {

            quotaActionText.textContent =
              'Sign In';

          }

        } else if (
          creditsVariantId ||
          creditsUrl
        ) {

          buyCreditsLink.hidden =
            false;


          buyCreditsLink.classList.add(
            'is-checkout'
          );


          buyCreditsLink.dataset.quotaActionType =
            'checkout';


          /*
           * If the product picker is configured, JS will
           * intercept this and go straight to checkout.
           * The normal product URL remains a safe fallback.
           */

          buyCreditsLink.href =
            creditsUrl ||
            '#';


          if (
            quotaActionText
          ) {

            quotaActionText.textContent =
              creditsPrice
                ? `Get More Generations · ${creditsPrice}`
                : 'Get More Generations';

          }

        } else {

          buyCreditsLink.hidden =
            true;

        }

      }


      /*
       * The backend remains the final authority.
       * This disabled state is only UX.
       */

      if (
        generateButton
      ) {

        const shouldBlock =
          !canGenerate;


        generateButton.disabled =
          shouldBlock;


        generateButton.classList.toggle(
          'is-quota-blocked',
          shouldBlock
        );

      }


      if (
        generateButtonText &&
        !generateButton?.classList.contains(
          'is-loading'
        )
      ) {

        generateButtonText.textContent =
          canGenerate
            ? 'Create Your Bondverse'
            : 'Daily Limit Reached';

      }

    }



    async function restoreSavedGeneration() {

      try {

        const saved =
          await getBondverseSession(
            browserStorageKey
          );


        if (
          !saved ||
          !saved.generationId
        ) {

          /*
           * No saved generation exists.
           * A stale result hash should not leave
           * the customer on an empty result page.
           */

          if (
            window.location.hash ===
            '#your-bondverse'
          ) {

            const cleanUrl =
              window.location.pathname +
              window.location.search;


            window.history.replaceState(
              {
                bondversePage:
                  'create'
              },
              '',
              cleanUrl
            );

          }


          showCreatePage({
            smooth: false
          });


          return;

        }



        /* -----------------------------
           RESTORE ORIGINAL PHOTO
        ------------------------------ */

        savedOriginalPhotoBlob =
          saved.originalPhotoBlob ||
          null;


        savedOriginalPhotoName =
          saved.originalPhotoName ||
          'Saved photo';


        if (
          savedOriginalPhotoBlob
        ) {

          setOriginalPhotoPreview(
            savedOriginalPhotoBlob,
            savedOriginalPhotoName
          );

        }



        /* -----------------------------
           RESTORE GENERATED RESULT
        ------------------------------ */

        generatedState = {

          image:
            saved.image ||
            saved.artworkUrl ||
            '',

          artworkUrl:
            saved.artworkUrl ||
            '',

          originalPhoto:
            currentPhotoObjectUrl ||
            '',

          generationId:
            saved.generationId ||
            '',

          vibe:
            saved.vibe ||
            'Romantic',

          bondSpecial:
            saved.bondSpecial ||
            '',

          universeDetails:
            saved.universeDetails ||
            ''

        };


        if (
          generatedPreview &&
          generatedState.image
        ) {

          generatedPreview.src =
            generatedState.image;


          generatedPreview.hidden =
            false;

        }


        if (
          exampleArtwork &&
          generatedState.image
        ) {

          exampleArtwork.hidden =
            true;

        }


        if (
          generationIdInput
        ) {

          generationIdInput.value =
            generatedState.generationId;

        }


        if (
          form
        ) {

          form.dataset.generationId =
            generatedState.generationId;

        }



        /* -----------------------------
           RESTORE FORM ANSWERS
        ------------------------------ */

        restoreFormValues(
          generatedState
        );



        /* -----------------------------
           RESTORE RESULT OPTIONS
        ------------------------------ */

        const savedPackageQuantity =
          Number(
            saved.packageQuantity ||
            (
              saved.matchingSet
                ? (Number(saved.quantity) || 1) * 2
                : (Number(saved.quantity) || 1)
            )
          );


        setQuantity(
          savedPackageQuantity
        );



        /* -----------------------------
           BUILD RESULT
        ------------------------------ */

        populateResultPage(
          generatedState,
          {
            preserveOptions:
              true
          }
        );



        /* -----------------------------
           RESTORE CURRENT ROUTE
        ------------------------------ */

        if (
          window.location.hash ===
          '#your-bondverse'
        ) {

          showResultPage({
            pushHistory: false,
            smooth: false
          });

        } else {

          /*
           * If they refreshed while editing,
           * keep them on the create screen,
           * but all saved data remains restored.
           */

          showCreatePage({
            smooth: false
          });


          showMessage(
            'Your saved Bondverse has been restored ✓',
            'success'
          );

        }


        console.log(
          '[Multiverse Bonds] Restored saved generation:',
          generatedState.generationId
        );


      } catch (
        error
      ) {

        console.warn(
          '[Multiverse Bonds] Browser restore unavailable:',
          error
        );


        if (
          window.location.hash ===
          '#your-bondverse'
        ) {

          const cleanUrl =
            window.location.pathname +
            window.location.search;


          window.history.replaceState(
            {
              bondversePage:
                'create'
            },
            '',
            cleanUrl
          );

        }


        showCreatePage({
          smooth: false
        });

      }

    }



    async function saveCurrentGenerationToBrowser() {

      if (
        !generatedState.generationId ||
        !generatedState.image
      ) {

        return;

      }


      const session = {

        key:
          browserStorageKey,

        version:
          1,

        savedAt:
          Date.now(),

        pathname:
          window.location.pathname,

        image:
          generatedState.image,

        artworkUrl:
          generatedState.artworkUrl,

        generationId:
          generatedState.generationId,

        vibe:
          generatedState.vibe,

        bondSpecial:
          generatedState.bondSpecial,

        universeDetails:
          generatedState.universeDetails,

        originalPhotoBlob:
          savedOriginalPhotoBlob,

        originalPhotoName:
          savedOriginalPhotoName,

        quantity:
          getQuantity(),

        packageQuantity:
          getQuantity(),

        matchingSet:
          getQuantity() > 1

      };


      await saveBondverseSession(
        session
      );

    }



    async function saveCurrentGenerationToBrowserQuietly() {

      if (
        !generatedState.generationId
      ) {

        return;

      }


      try {

        await saveCurrentGenerationToBrowser();

      } catch (
        error
      ) {

        console.warn(
          '[Multiverse Bonds] Could not update browser-saved options:',
          error
        );

      }

    }



    function restoreFormValues(
      data
    ) {

      if (
        !form
      ) {

        return;

      }


      const vibeRadio =
        Array.from(
          form.querySelectorAll(
            'input[name="relationship_vibe"]'
          )
        )
          .find(
            radio =>
              radio.value ===
              data.vibe
          );


      if (
        vibeRadio
      ) {

        vibeRadio.checked =
          true;

      }


      const bondField =
        form.querySelector(
          '[name="bond_special"]'
        );


      if (
        bondField
      ) {

        bondField.value =
          data.bondSpecial ||
          '';


        bondField.dispatchEvent(
          new Event(
            'input',
            {
              bubbles: true
            }
          )
        );

      }


      const universeField =
        form.querySelector(
          '[name="universe_details"]'
        );


      if (
        universeField
      ) {

        universeField.value =
          data.universeDetails ||
          '';


        universeField.dispatchEvent(
          new Event(
            'input',
            {
              bubbles: true
            }
          )
        );

      }

    }



    function setOriginalPhotoPreview(
      blob,
      name = 'Saved photo'
    ) {

      if (
        currentPhotoObjectUrl
      ) {

        URL.revokeObjectURL(
          currentPhotoObjectUrl
        );

      }


      currentPhotoObjectUrl =
        URL.createObjectURL(
          blob
        );


      if (
        uploadedPreview
      ) {

        uploadedPreview.src =
          currentPhotoObjectUrl;


        uploadedPreview.hidden =
          false;

      }


      if (
        examplePhoto
      ) {

        examplePhoto.hidden =
          true;

      }


      if (
        uploadTitle
      ) {

        uploadTitle.textContent =
          name;

      }


      generatedState.originalPhoto =
        currentPhotoObjectUrl;

    }



    function hideCurrentGeneratedPreview() {

      if (
        generatedPreview
      ) {

        generatedPreview.hidden =
          true;

      }


      if (
        exampleArtwork
      ) {

        exampleArtwork.hidden =
          false;

      }

    }



    function populateResultPage(
      data,
      options = {}
    ) {

      const preserveOptions =
        Boolean(
          options.preserveOptions
        );


      if (
        resultImage
      ) {

        resultImage.src =
          data.image;


        resultImage.alt =
          'Your personalized Bondverse Couple Keychain';

      }


      if (
        resultThumb
      ) {

        resultThumb.src =
          data.image;

      }


      if (
        resultOriginal &&
        data.originalPhoto
      ) {

        resultOriginal.src =
          data.originalPhoto;

      }


      if (
        resultVibe
      ) {

        resultVibe.textContent =
          data.vibe ||
          'Personalized';

      }


      if (
        resultStory
      ) {

        resultStory.textContent =
          data.bondSpecial ||
          '';

      }


      if (
        resultStoryRow
      ) {

        resultStoryRow.hidden =
          !data.bondSpecial;

      }


      if (
        resultUniverse
      ) {

        resultUniverse.textContent =
          data.universeDetails ||
          '';

      }


      if (
        resultUniverseRow
      ) {

        resultUniverseRow.hidden =
          !data.universeDetails;

      }


      if (
        resultGenerationId
      ) {

        resultGenerationId.value =
          data.generationId ||
          '';

      }


      resultViewButtons.forEach(
        button => {

          button.classList.toggle(
            'is-active',
            button.dataset.resultView ===
              'art'
          );

        }
      );


      if (
        !preserveOptions
      ) {

        setQuantity(
          2
        );

      }


      if (
        cartMessage
      ) {

        cartMessage.classList.remove(
          'is-visible',
          'is-info',
          'is-success',
          'is-error'
        );


        cartMessage.textContent =
          '';

      }

    }



    function showResultPage({
      pushHistory = false,
      smooth = true
    } = {}) {

      if (
        !generatedState.image
      ) {

        return;

      }


      /*
       * Keep the normal Shopify header visible.
       * The result screen now behaves like a standard ecommerce PDP.
       */


      if (
        createStep
      ) {

        createStep.hidden =
          true;

      }


      if (
        resultStep
      ) {

        resultStep.hidden =
          false;

      }


      /*
       * The saved-return bar only belongs on
       * the personalization/create screen.
       */

      if (
        savedReturnBar
      ) {

        savedReturnBar.hidden =
          true;

      }


      if (
        pushHistory &&
        window.location.hash !==
          '#your-bondverse'
      ) {

        window.history.pushState(
          {
            bondversePage:
              'result'
          },
          '',
          '#your-bondverse'
        );

      }


      scrollToSection(
        smooth
      );

    }



    function showCreatePage({
      smooth = true
    } = {}) {

      /*
       * Standard Shopify header remains visible on both screens.
       */


      if (
        resultStep
      ) {

        resultStep.hidden =
          true;

      }


      if (
        createStep
      ) {

        createStep.hidden =
          false;

      }


      /*
       * When a completed generation exists,
       * always give the customer a clear path
       * back to the finished product page.
       */

      if (
        savedReturnBar
      ) {

        savedReturnBar.hidden =
          !generatedState.image;

      }


      scrollToSection(
        smooth
      );

    }



    function scrollToSection(
      smooth
    ) {

      const top =
        section.getBoundingClientRect().top +
        window.scrollY;


      window.scrollTo({
        top:
          Math.max(
            top,
            0
          ),

        behavior:
          smooth
            ? 'smooth'
            : 'auto'
      });

    }



    function getQuantity() {

      const selectedPackage =
        Array.from(
          packageInputs
        ).find(
          input => input.checked
        );


      const parsed =
        parseInt(
          selectedPackage?.value || '1',
          10
        );


      return [1, 2, 4].includes(
        parsed
      )
        ? parsed
        : 1;

    }



    function setQuantity(
      value
    ) {

      const requested =
        Number(value) ||
        1;


      const normalized =
        [1, 2, 4].includes(
          requested
        )
          ? requested
          : 1;


      packageInputs.forEach(
        input => {

          input.checked =
            Number(
              input.value
            ) === normalized;

        }
      );

    }



    function isSupportedImage(
      file
    ) {

      if (
        !file
      ) {

        return false;

      }


      const supportedMimeTypes =
        new Set([
          'image/jpeg',
          'image/png',
          'image/webp',
          'image/heic',
          'image/heif'
        ]);


      if (
        supportedMimeTypes.has(
          file.type
        )
      ) {

        return true;

      }


      /*
       * A restored Blob may not have a filename.
       */

      if (
        !file.name
      ) {

        return Boolean(
          file.type &&
          file.type.startsWith(
            'image/'
          )
        );

      }


      const extension =
        (
          file.name
            .split('.')
            .pop() ||
          ''
        )
          .toLowerCase();


      return [
        'jpg',
        'jpeg',
        'png',
        'webp',
        'heic',
        'heif'
      ].includes(
        extension
      );

    }



    function resetEverythingForNewGeneration() {

      /*
       * A fresh generation starts back in the normal
       * customization experience, so restore the
       * Shopify theme header immediately.
       */

      document.body.classList.remove(
        'bondverse-result-open'
      );


      /*
       * Release old local Blob URL.
       */

      if (
        currentPhotoObjectUrl
      ) {

        URL.revokeObjectURL(
          currentPhotoObjectUrl
        );


        currentPhotoObjectUrl =
          null;

      }


      savedOriginalPhotoBlob =
        null;


      savedOriginalPhotoName =
        '';


      generatedState = {

        image:
          '',

        artworkUrl:
          '',

        originalPhoto:
          '',

        generationId:
          '',

        vibe:
          '',

        bondSpecial:
          '',

        universeDetails:
          ''

      };


      if (
        savedReturnBar
      ) {

        savedReturnBar.hidden =
          true;

      }


      if (
        form
      ) {

        form.reset();


        delete form.dataset.generationId;

      }


      if (
        photoInput
      ) {

        photoInput.value =
          '';

      }


      if (
        uploadTitle
      ) {

        uploadTitle.textContent =
          'Upload your favorite photo';

      }


      if (
        uploadedPreview
      ) {

        uploadedPreview.hidden =
          true;


        uploadedPreview.removeAttribute(
          'src'
        );

      }


      if (
        examplePhoto
      ) {

        examplePhoto.hidden =
          false;

      }


      if (
        generatedPreview
      ) {

        generatedPreview.hidden =
          true;


        generatedPreview.removeAttribute(
          'src'
        );

      }


      if (
        exampleArtwork
      ) {

        exampleArtwork.hidden =
          false;

      }


      if (
        generationLoading
      ) {

        generationLoading.hidden =
          true;

      }


      if (
        generationIdInput
      ) {

        generationIdInput.value =
          '';

      }


      if (
        resultImage
      ) {

        resultImage.removeAttribute(
          'src'
        );

      }


      if (
        resultThumb
      ) {

        resultThumb.removeAttribute(
          'src'
        );

      }


      if (
        resultOriginal
      ) {

        resultOriginal.removeAttribute(
          'src'
        );

      }


      setQuantity(
        2
      );


      section
        .querySelectorAll(
          '[data-character-field]'
        )
        .forEach(
          field => {

            field.dispatchEvent(
              new Event(
                'input',
                {
                  bubbles: true
                }
              )
            );

          }
        );


      if (
        formMessage
      ) {

        formMessage.textContent =
          '';


        formMessage.classList.remove(
          'is-visible',
          'is-info',
          'is-success',
          'is-error'
        );

      }


      if (
        cartMessage
      ) {

        cartMessage.textContent =
          '';


        cartMessage.classList.remove(
          'is-visible',
          'is-info',
          'is-success',
          'is-error'
        );

      }

    }



    function setGenerating(
      isGenerating
    ) {

      if (
        generateButton
      ) {

        const quotaBlocked =
          Boolean(
            quotaState &&
            quotaState.can_generate ===
              false
          );


        generateButton.disabled =
          isGenerating ||
          quotaBlocked;


        generateButton.classList.toggle(
          'is-loading',
          isGenerating
        );


        generateButton.classList.toggle(
          'is-quota-blocked',
          !isGenerating &&
          quotaBlocked
        );

      }


      if (
        generateButtonText
      ) {

        const quotaBlocked =
          Boolean(
            quotaState &&
            quotaState.can_generate ===
              false
          );


        generateButtonText.textContent =
          isGenerating
            ? 'Creating Your Universe...'
            : (
                quotaBlocked
                  ? 'Daily Limit Reached'
                  : 'Create Your Bondverse'
              );

      }


      if (
        generationLoading
      ) {

        generationLoading.hidden =
          !isGenerating;

      }


      if (
        isGenerating &&
        generatedPreview
      ) {

        generatedPreview.hidden =
          true;

      }


      if (
        isGenerating &&
        exampleArtwork
      ) {

        exampleArtwork.hidden =
          true;

      }

    }



    function showMessage(
      message,
      type = 'info'
    ) {

      if (
        !formMessage
      ) {

        return;

      }


      formMessage.textContent =
        message;


      formMessage.classList.remove(
        'is-info',
        'is-success',
        'is-error'
      );


      formMessage.classList.add(
        'is-visible',
        `is-${type}`
      );

    }



    function showCartMessage(
      message,
      type = 'info'
    ) {

      if (
        !cartMessage
      ) {

        return;

      }


      cartMessage.textContent =
        message;


      cartMessage.classList.remove(
        'is-info',
        'is-success',
        'is-error'
      );


      cartMessage.classList.add(
        'is-visible',
        `is-${type}`
      );

    }



    async function parseShopifyResponse(
      response
    ) {

      const contentType =
        response.headers.get(
          'content-type'
        ) ||
        '';


      if (
        contentType.includes(
          'application/json'
        )
      ) {

        return await response.json();

      }


      const text =
        await response.text();


      return {

        description:
          text
            ? `Shopify returned an unexpected response (${response.status}).`
            : `Shopify cart request failed (${response.status}).`

      };

    }



    async function parseApiResponse(
      response
    ) {

      const contentType =
        response.headers.get(
          'content-type'
        ) ||
        '';


      if (
        contentType.includes(
          'application/json'
        )
      ) {

        return await response.json();

      }


      const text =
        await response.text();


      return {

        detail:
          text
            ? `Backend returned an unexpected response (${response.status}).`
            : `Backend request failed (${response.status}).`

      };

    }

  }

})();
